import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ListingShopPricesModal } from "./ListingShopPricesModal";

interface ShopListingInfo {
  id: string;
  shopName: string;
  listingUrl: string;
  price: number | null;
}

interface Props {
  shopListings: ShopListingInfo[];
  listingPrice: number | null;
  condition: string;
  conditionLabel?: string | null;
}

const MIN_SAVINGS_PCT = 0.15;
const MIN_SAVINGS_EUR = 2;

function formatEuro(value: number): string {
  return value % 1 === 0 ? `${value.toFixed(0)}` : value.toFixed(2);
}

export function ListingPriceComparison({
  shopListings,
  listingPrice,
  condition,
  conditionLabel,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const pricedShops = shopListings.filter(
    (s): s is ShopListingInfo & { price: number } => s.price != null,
  );
  if (pricedShops.length === 0 || listingPrice == null) return null;

  const cheapestNew = Math.min(...pricedShops.map((s) => s.price));
  const savingsVsNew = cheapestNew - listingPrice;
  const savingsPct = savingsVsNew / cheapestNew;
  const showSavings =
    savingsVsNew >= MIN_SAVINGS_EUR && savingsPct >= MIN_SAVINGS_PCT;

  // Suppress unused warning while keeping API stable
  void condition;

  return (
    <div className="mt-2 space-y-1.5">
      {showSavings && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-800 transition-colors hover:bg-emerald-200"
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
          −{Math.round(savingsPct * 100)}% vs. parduotuvėje ({formatEuro(cheapestNew)} €)
        </button>
      )}

      {!showSavings && (
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <span>
            {t("listings.cheapest_new_price", {
              price: formatEuro(cheapestNew),
            })}
          </span>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex h-4 w-4 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-primary-700"
            title={t("listings.shop_prices_modal_title")}
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      )}

      <ListingShopPricesModal
        open={open}
        onClose={() => setOpen(false)}
        shopListings={shopListings}
        listingPrice={listingPrice}
        conditionLabel={conditionLabel}
      />
    </div>
  );
}
